import type { ReactNode } from 'react';
import { ApiError } from '../lib/api';

export function Card({
  title,
  subtitle,
  actions,
  children,
  className = '',
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`card ${className}`}>
      {(title || actions) && (
        <header className="mb-3 flex flex-wrap items-start justify-between gap-2">
          <div>
            {title && <h2 className="text-base font-semibold">{title}</h2>}
            {subtitle && (
              <p className="mt-0.5 text-sm text-ink-muted dark:text-ink-inverse-muted">{subtitle}</p>
            )}
          </div>
          {actions && <div className="flex gap-2">{actions}</div>}
        </header>
      )}
      {children}
    </section>
  );
}

export type Tone = 'info' | 'warn' | 'critical' | 'good';

const TONE_STYLES: Record<Tone, string> = {
  info: 'border-brand/30 bg-brand-soft text-brand-strong dark:bg-brand/10 dark:text-brand-soft',
  good: 'border-good/30 bg-good-soft text-good dark:bg-good/10 dark:text-good-soft',
  warn: 'border-warn/30 bg-warn-soft text-warn dark:bg-warn/10 dark:text-warn-soft',
  critical: 'border-bad/30 bg-bad-soft text-bad dark:bg-bad/10 dark:text-bad-soft',
};

/**
 * Severity is carried by an icon and a label as well as by colour — colour
 * alone fails both colour-blind users and greyscale printing (§9, WCAG 1.4.1).
 */
const TONE_ICON: Record<Tone, string> = {
  info: 'ⓘ',
  good: '✓',
  warn: '⚠',
  critical: '✕',
};

const TONE_LABEL: Record<Tone, string> = {
  info: 'Note',
  good: 'Good',
  warn: 'Warning',
  critical: 'Critical',
};

export function Alert({
  tone = 'info',
  title,
  children,
  action,
}: {
  tone?: Tone;
  title?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div
      className={`rounded-md border p-3 text-sm ${TONE_STYLES[tone]}`}
      role={tone === 'critical' ? 'alert' : 'status'}
    >
      <div className="flex gap-2">
        <span aria-hidden="true" className="select-none font-semibold">
          {TONE_ICON[tone]}
        </span>
        <div className="min-w-0 flex-1">
          <span className="sr-only">{TONE_LABEL[tone]}: </span>
          {title && <p className="font-semibold">{title}</p>}
          {children && <div className={title ? 'mt-1' : ''}>{children}</div>}
          {action && <div className="mt-2">{action}</div>}
        </div>
      </div>
    </div>
  );
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 text-sm text-ink-muted dark:text-ink-inverse-muted" role="status">
      <span
        aria-hidden="true"
        className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
      />
      {label}…
    </div>
  );
}

/** Skeleton block for a first load, so the layout does not jump. */
export function Skeleton({ className = 'h-24' }: { className?: string }) {
  return (
    <div
      className={`animate-pulse rounded-md bg-black/5 dark:bg-white/5 ${className}`}
      aria-hidden="true"
    />
  );
}

export function ErrorState({
  error,
  onRetry,
  what = 'this section',
}: {
  error: Error;
  onRetry?: () => void;
  what?: string;
}) {
  const message =
    error instanceof ApiError && error.message
      ? error.message
      : `Could not load ${what}. Please try again.`;

  return (
    <Alert
      tone="critical"
      title={`Could not load ${what}`}
      action={
        onRetry && (
          <button type="button" className="btn-secondary" onClick={onRetry}>
            Try again
          </button>
        )
      }
    >
      <p>{message}</p>
      {error instanceof ApiError && typeof error.details.ref === 'string' && (
        <p className="mt-1 text-xs opacity-80">Reference: {error.details.ref}</p>
      )}
    </Alert>
  );
}

export function EmptyState({
  title,
  children,
  action,
}: {
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="rounded-md border border-dashed border-black/15 p-6 text-center dark:border-white/15">
      <p className="font-medium">{title}</p>
      {children && (
        <div className="mx-auto mt-1 max-w-prose text-sm text-ink-muted dark:text-ink-inverse-muted">
          {children}
        </div>
      )}
      {action && <div className="mt-3 flex justify-center">{action}</div>}
    </div>
  );
}

let fieldSeq = 0;

export function Field({
  label,
  hint,
  error,
  children,
  id,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: string;
  children: (props: { id: string; 'aria-describedby': string | undefined; 'aria-invalid': boolean }) => ReactNode;
  id?: string;
}) {
  const fieldId = id ?? `field-${++fieldSeq}`;
  const hintId = hint ? `${fieldId}-hint` : undefined;
  const errorId = error ? `${fieldId}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div>
      <label className="label" htmlFor={fieldId}>
        {label}
      </label>
      {children({ id: fieldId, 'aria-describedby': describedBy, 'aria-invalid': Boolean(error) })}
      {hint && (
        <p id={hintId} className="mt-1 text-xs text-ink-muted dark:text-ink-inverse-muted">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className="mt-1 text-xs font-medium text-bad">
          {error}
        </p>
      )}
    </div>
  );
}

/** A figure with its label, used across the summary cards. */
export function Stat({
  label,
  value,
  sub,
  tone,
}: {
  label: ReactNode;
  value: ReactNode;
  sub?: ReactNode;
  tone?: 'good' | 'bad';
}) {
  const toneClass = tone === 'good' ? 'text-good' : tone === 'bad' ? 'text-bad' : '';
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-ink-muted dark:text-ink-inverse-muted">
        {label}
      </p>
      <p className={`num mt-1 text-xl font-semibold ${toneClass}`}>{value}</p>
      {sub && <p className="mt-0.5 text-xs text-ink-muted dark:text-ink-inverse-muted">{sub}</p>}
    </div>
  );
}

/** Small pill, e.g. the stale-price badge. */
export function Badge({ tone = 'info', children }: { tone?: Tone; children: ReactNode }) {
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs ${TONE_STYLES[tone]}`}>
      {children}
    </span>
  );
}

/**
 * §12: shown at registration and on the Insights page. Not a footnote — this
 * app models scenarios, and mistaking a scenario for a forecast is the most
 * expensive error a user of it can make.
 */
export const DISCLAIMER_TEXT =
  'FIRE Tracker provides information and modelling, not financial advice. Projections are scenarios built from assumptions you control — they are not forecasts, and returns are not guaranteed. Consider consulting a SEBI-registered investment adviser before acting on anything you see here.';

export function Disclaimer({ compact = false }: { compact?: boolean }) {
  return (
    <div
      className={`rounded-md border border-black/10 bg-surface-muted p-3 text-xs text-ink-muted dark:border-white/10 dark:bg-surface-dark dark:text-ink-inverse-muted ${
        compact ? '' : 'sm:text-sm'
      }`}
    >
      <strong className="font-semibold">Not financial advice. </strong>
      {DISCLAIMER_TEXT.replace('FIRE Tracker provides information and modelling, not financial advice. ', '')}
    </div>
  );
}
